import React, { memo, useMemo, useState } from 'react';
import { Sidebar } from './components';
import { Navigation } from './components/Sidebar/Sidebar';
import {
  IconAccount,
  IconBookMark,
  IconBrickWall24,
  IconInfo24,
  IconMoneyBag24,
  IconNotificationSidebar24,
  IconScrewDriver24,
  IconSpecialist,
  IconTenderIcon,
  IconTruck24,
} from '@app/icons';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useUserContext } from 'providers/UserProvider';
import { UserType } from '@app/models';
import {
  AppRoutesUserType,
  routeConfigByTypeProfile,
} from 'config/routeConfig/profile';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import Header from 'components/Header/Header';

interface Props {}

function ProfilePage(props: Props) {
  const { user, currentProfile } = useUserContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigationItems = useMemo<Navigation[]>(() => {
    const returnNavigationItems: Navigation[] = [
      {
        title: 'Главная',
        path: '/',
        icon: IconInfo24,
      },
      {
        title: 'Профиль',
        path:
          currentProfile.currentType === 'user'
            ? '/profile'
            : `/profile/company/${currentProfile.bin}`,
        icon: IconAccount,
      },
    ];

    if (
      currentProfile.currentType === 'user' &&
      currentProfile.type !== UserType.INDIVIDUAL
    ) {
      return [...returnNavigationItems];
    }

    if (currentProfile.blocked) {
      return [
        ...returnNavigationItems,
        {
          title: 'Уведомление ',
          path: '/profile/notification',
          icon: IconNotificationSidebar24,
        },
      ];
    }

    returnNavigationItems.push({
      title: 'СМР',
      path: '/profile/tender/contractor',
      icon: IconScrewDriver24,
    });

    returnNavigationItems.push({
      title: 'Материалы',
      path: '/profile/tender/supplier',
      icon: IconBrickWall24,
    });

    return [
      ...returnNavigationItems,
      {
        title: 'Спецтехника',
        path: '/profile/vehicles',
        icon: IconTruck24,
      },
      {
        title: 'Специалисты ',
        path: '/profile/specialists',
        icon: IconSpecialist,
      },
      {
        title: 'Партнеры',
        path: '/profile/partners',
        icon: IconMoneyBag24,
      },
      {
        title: 'Тендеры',
        path: '/profile/gov-tenders',
        icon: IconTenderIcon,
      },
      {
        title: 'Избранное',
        path: '/profile/favorites',
        icon: IconBookMark,
      },
      {
        title: 'Уведомление ',
        path: '/profile/notification',
        icon: IconNotificationSidebar24,
      },
    ];
  }, [currentProfile, user.type]);

  const userType = (): AppRoutesUserType => {
    if (user.type === UserType.INDIVIDUAL) {
      return AppRoutesUserType.INDIVIDUAL;
    }
    if (
      user.type === UserType.ENTITY &&
      currentProfile.currentType === 'company'
    ) {
      return AppRoutesUserType.ENTITY;
    }
    return AppRoutesUserType.ENTITY_INDIVIDUAL_USER;
  };

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      <Header />
      <div className="flex-1 flex flex-row relative overflow-hidden">
        <aside
          className={cn(
            'fixed top-16 bottom-0 left-0 z-40 w-64 bg-white shadow-[1px_0_8px_rgba(0,0,0,0.1)] transition-transform duration-300 flex-shrink-0 md:static md:block -translate-x-full md:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            'md:translate-x-0'
          )}
        >
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto">
              <ul className="p-0 m-4 mt-0 list-none bg-white">
                <Sidebar data={navigationItems} />
              </ul>
            </div>
          </div>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-20 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Button
          variant="outline"
          size="sm"
          className={cn(
            'fixed top-1/2 -translate-y-1/2 z-30 md:hidden',
            'h-20 rounded-r-lg rounded-l-none border-l-0',
            '!bg-primary hover:!bg-primary/90 !border-primary shadow-lg',
            'flex flex-col items-center justify-center gap-1 px-2',
            sidebarOpen ? 'left-64' : 'left-0',
            'transition-all duration-300'
          )}
          onClick={() => setSidebarOpen((v) => !v)}
        >
          <svg
            className={cn(
              'w-5 h-5 text-white transition-transform duration-300',
              sidebarOpen ? 'rotate-180' : 'rotate-0'
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
          <span className="text-[10px] font-medium text-white leading-tight whitespace-nowrap">
            {sidebarOpen ? 'Свернуть' : 'Меню'}
          </span>
        </Button>

        <main className="flex-1 overflow-y-auto py-4 ml-0 md:ml-10">
          <div className="w-full max-w-none mx-0 px-4 md:px-0 pb-8">
            <Routes>
              {Object.values(routeConfigByTypeProfile[userType()]).map(
                ({ element, path }) => (
                  <Route key={path} path={path} element={element} />
                )
              )}
              {userType() === AppRoutesUserType.ENTITY ? (
                <Route
                  path="*"
                  element={<Navigate replace to="/profile/company" />}
                />
              ) : (
                <Route path="*" element={<Navigate replace to="/profile" />} />
              )}
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

export default memo(ProfilePage);
